import { log } from '../utils/aifc-logging';

// ADF Document Node
interface DocNode {
	type: 'doc';
	version: number;
	content: Array<any>;
}

// Tokenizer Types
type TokenType =
	| 'STRING'
	| 'NUMBER'
	| 'BOOLEAN'
	| 'NULL'
	| 'LEFT_BRACE'
	| 'RIGHT_BRACE'
	| 'LEFT_BRACKET'
	| 'RIGHT_BRACKET'
	| 'COLON'
	| 'COMMA'
	| 'EOF';

interface Token {
	type: TokenType;
	value: string;
	position: number;
}

class AdfTokenizer {
	private input: string;
	private position: number = 0;

	constructor(input: string) {
		this.input = input;
	}

	private isWhitespace(char: string): boolean {
		return [' ', '\n', '\t', '\r'].includes(char);
	}

	private isDigit(char: string): boolean {
		return char >= '0' && char <= '9';
	}

	private isAlpha(char: string): boolean {
		return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z');
	}

	private peek(): string {
		return this.position < this.input.length ? this.input[this.position] || '\0' : '\0';
	}

	private advance(): string {
		if (this.position < this.input.length) {
			return this.input[this.position++] || '\0';
		}
		return '\0';
	}

	private skipWhitespace(): void {
		while (this.position < this.input.length && this.isWhitespace(this.peek())) {
			this.advance();
		}
	}

	private readString(): Token {
		const start = this.position;
		this.advance(); // consume opening quote

		let value = '';
		while (this.position < this.input.length && this.peek() !== '"') {
			if (this.peek() === '\\') {
				this.advance(); // consume backslash
				const escaped = this.advance();
				switch (escaped) {
					case '"':
						value += '"';
						break;
					case '\\':
						value += '\\';
						break;
					case '/':
						value += '/';
						break;
					case 'b':
						value += '\b';
						break;
					case 'f':
						value += '\f';
						break;
					case 'n':
						value += '\n';
						break;
					case 'r':
						value += '\r';
						break;
					case 't':
						value += '\t';
						break;
					default:
						value += escaped;
						break;
				}
			} else {
				value += this.advance();
			}
		}

		if (this.peek() !== '"') {
			throw new Error(`Unterminated string at position ${this.position}`);
		}
		this.advance(); // consume closing quote

		return {
			type: 'STRING',
			value,
			position: start,
		};
	}

	private readNumber(): Token {
		const start = this.position;
		let value = '';

		// Handle negative sign
		if (this.peek() === '-') {
			value += this.advance();
		}

		// Handle integer part
		if (this.peek() === '0') {
			value += this.advance();
		} else if (this.isDigit(this.peek())) {
			while (this.isDigit(this.peek())) {
				value += this.advance();
			}
		} else {
			throw new Error(`Invalid number at position ${this.position}`);
		}

		// Handle decimal part
		if (this.peek() === '.') {
			value += this.advance();
			if (!this.isDigit(this.peek())) {
				throw new Error(`Invalid number at position ${this.position}`);
			}
			while (this.isDigit(this.peek())) {
				value += this.advance();
			}
		}

		// Handle exponent
		if (this.peek() === 'e' || this.peek() === 'E') {
			value += this.advance();
			if (this.peek() === '+' || this.peek() === '-') {
				value += this.advance();
			}
			if (!this.isDigit(this.peek())) {
				throw new Error(`Invalid number at position ${this.position}`);
			}
			while (this.isDigit(this.peek())) {
				value += this.advance();
			}
		}

		return {
			type: 'NUMBER',
			value,
			position: start,
		};
	}

	private readLiteral(): Token {
		const start = this.position;
		let value = '';

		while (this.position < this.input.length && this.isAlpha(this.peek())) {
			value += this.advance();
		}

		let type: TokenType;
		switch (value) {
			case 'true':
			case 'false':
				type = 'BOOLEAN';
				break;
			case 'null':
				type = 'NULL';
				break;
			default:
				throw new Error(`Invalid literal '${value}' at position ${start}`);
		}

		return {
			type,
			value,
			position: start,
		};
	}

	nextToken(): Token {
		this.skipWhitespace();

		if (this.position >= this.input.length) {
			return { type: 'EOF', value: '', position: this.position };
		}

		const char = this.peek();

		switch (char) {
			case '{':
				this.advance();
				return { type: 'LEFT_BRACE', value: char, position: this.position - 1 };
			case '}':
				this.advance();
				return { type: 'RIGHT_BRACE', value: char, position: this.position - 1 };
			case '[':
				this.advance();
				return { type: 'LEFT_BRACKET', value: char, position: this.position - 1 };
			case ']':
				this.advance();
				return { type: 'RIGHT_BRACKET', value: char, position: this.position - 1 };
			case ':':
				this.advance();
				return { type: 'COLON', value: char, position: this.position - 1 };
			case ',':
				this.advance();
				return { type: 'COMMA', value: char, position: this.position - 1 };
			case '"':
				return this.readString();
			case '-':
			case '0':
			case '1':
			case '2':
			case '3':
			case '4':
			case '5':
			case '6':
			case '7':
			case '8':
			case '9':
				return this.readNumber();
			default:
				if (this.isAlpha(char)) {
					return this.readLiteral();
				}
				throw new Error(`Unexpected character '${char}' at position ${this.position}`);
		}
	}
}

export class AdfParser {
	private tokenizer: AdfTokenizer;
	private currentToken: Token;
	private repairs: Array<{
		type: string;
		from: string;
		to: string;
		context: string;
		expansion?: number;
	}> = [];

	constructor(input: string) {
		this.tokenizer = new AdfTokenizer(input);
		this.currentToken = this.tokenizer.nextToken();
	}

	getRepairs() {
		return this.repairs;
	}

	private advance(): void {
		this.currentToken = this.tokenizer.nextToken();
	}

	private expect(tokenType: TokenType): void {
		if (this.currentToken.type !== tokenType) {
			throw new Error(
				`Expected ${tokenType}, got ${this.currentToken.type} at position ${this.currentToken.position}`,
			);
		}
	}

	private wrapNodeWithType(wrapperType: string): (node: any) => any {
		return (node) => ({ type: wrapperType, content: [node] });
	}

	private convertMarkNodeToText(): (node: any) => any {
		return (node) => ({
			type: 'paragraph',
			content: [{ type: 'text', text: node.text || '', marks: [{ type: node.type }] }],
		});
	}

	private parseDoc(): DocNode {
		this.expect('LEFT_BRACE');
		this.advance();

		let version: number | undefined;
		let type: string | undefined;
		let content: any[] | undefined;

		while (this.currentToken.type !== 'RIGHT_BRACE' && this.currentToken.type !== 'EOF') {
			this.expect('STRING');
			const key = this.currentToken.value as string;
			this.advance();

			this.expect('COLON');
			this.advance();

			if (key === 'version') {
				this.expect('NUMBER');
				version = Number(this.currentToken.value);
				this.advance();
			} else if (key === 'type') {
				this.expect('STRING');
				type = this.currentToken.value as string;
				if (type !== 'doc') {
					throw new Error(
						`Expected "doc" type, got "${type}" at position ${this.currentToken.position}`,
					);
				}
				this.advance();
			} else if (key === 'content') {
				this.expect('LEFT_BRACKET');
				this.advance();
				content = this.parseContent('doc');
				if (this.currentToken.type === 'RIGHT_BRACKET') {
					this.advance();
				}
			} else {
				this.skipValue();
			}

			if (this.currentToken.type === 'COMMA') {
				this.advance();
			}
		}

		if (this.currentToken.type === 'RIGHT_BRACE') {
			this.advance();
		}

		return {
			type: 'doc',
			version: version ?? 1,
			content: content ?? [],
		};
	}

	// Schema validation with auto-repair strategies
	private getSchemaRules(parentType: string | undefined) {
		type SchemaRule = {
			valid: string[];
			repair?: Array<{
				nodes: string[];
				repair: (node: any) => any | null;
			}>;
		};

		const inlineNodes = [
			'date',
			'emoji',
			'hardBreak',
			'image',
			'inlineCard',
			'inlineExtension',
			'mediaInline',
			'mention',
			'placeholder',
			'status',
			'text',
		];

		const allBlockNodes = [
			'blockCard',
			'blockTaskItem',
			'blockquote',
			'bodiedExtension',
			'bulletList',
			'codeBlock',
			'decisionItem',
			'decisionList',
			'embedCard',
			'expand',
			'extension',
			'heading',
			'layoutColumn',
			'layoutSection',
			'listItem',
			'media',
			'mediaGroup',
			'mediaSingle',
			'nestedExpand',
			'orderedList',
			'panel',
			'paragraph',
			'rule',
			'table',
			'tableCell',
			'tableHeader',
			'tableRow',
			'taskItem',
			'taskList',
		];

		const schema: Record<string, SchemaRule | { valid: string[] | 'any' }> = {
			doc: {
				valid: allBlockNodes,
			},
			blockTaskItem: {
				valid: ['extension', 'paragraph'],
			},
			blockquote: {
				valid: [
					'bulletList',
					'codeBlock',
					'extension',
					'mediaGroup',
					'mediaSingle',
					'orderedList',
					'paragraph',
				],
			},
			bodiedExtension: {
				valid: allBlockNodes,
			},
			codeBlock: {
				valid: ['text'],
			},
			decisionItem: {
				valid: inlineNodes,
			},
			decisionList: {
				valid: ['decisionItem'],
			},
			expand: {
				valid: allBlockNodes,
			},
			heading: {
				valid: inlineNodes,
			},
			layoutColumn: {
				valid: allBlockNodes,
			},
			layoutSection: {
				valid: ['layoutColumn'],
			},
			listItem: {
				valid: [
					'bulletList',
					'codeBlock',
					'extension',
					'mediaSingle',
					'orderedList',
					'paragraph',
					'taskList',
				],
			},
			nestedExpand: {
				valid: allBlockNodes,
			},
			bulletList: {
				valid: ['listItem'],
				repair: [
					{
						nodes: ['bulletList', 'orderedList', 'taskList'],
						repair: this.wrapNodeWithType('listItem'),
					},
				],
			},
			orderedList: {
				valid: ['listItem'],
				repair: [
					{
						nodes: ['bulletList', 'orderedList', 'taskList'],
						repair: this.wrapNodeWithType('listItem'),
					},
				],
			},
			panel: {
				valid: allBlockNodes,
			},
			paragraph: {
				valid: inlineNodes,
				repair: [
					{
						nodes: ['strong', 'em', 'code', 'underline'],
						repair: (node) => ({ type: 'text', text: node.text, marks: [{ type: node.type }] }),
					},
				],
			},
			table: {
				valid: ['tableRow'],
			},
			tableCell: {
				valid: allBlockNodes,
				repair: [
					{
						nodes: ['strong', 'em', 'code', 'underline', 'strike'],
						repair: this.convertMarkNodeToText(),
					},
					{
						nodes: inlineNodes,
						repair: this.wrapNodeWithType('paragraph'),
					},
				],
			},
			tableHeader: {
				valid: allBlockNodes,
				repair: [
					{
						nodes: ['strong', 'em', 'code', 'underline', 'strike'],
						repair: this.convertMarkNodeToText(),
					},
					{
						nodes: inlineNodes,
						repair: this.wrapNodeWithType('paragraph'),
					},
				],
			},
			tableRow: {
				valid: ['tableCell', 'tableHeader'],
			},
			taskItem: {
				valid: inlineNodes,
				repair: [
					{
						nodes: ['paragraph'],
						repair: (node) => node.content,
					},
				],
			},
			taskList: {
				valid: allBlockNodes,
			},
		};

		return schema[parentType || 'doc'] || { valid: 'any' };
	}

	private isValidChildType(childType: string, parentType: string | undefined): boolean {
		const rules = this.getSchemaRules(parentType);

		if (rules.valid === 'any') {
			return true;
		}

		return rules.valid.includes(childType);
	}

	private tryRepairNode(node: any, parentType: string | undefined): any | null {
		const rules = this.getSchemaRules(parentType) as any;

		if (rules.repair && Array.isArray(rules.repair)) {
			for (const repairRule of rules.repair) {
				if (repairRule.nodes.includes(node.type)) {
					return repairRule.repair(node);
				}
			}
		}

		return null;
	}

	private countNodesInTree(node: any): number {
		let count = 1;

		// Also count marks (they're counted in extractTypesFromParsed)
		if (Array.isArray(node.marks)) {
			count += node.marks.length;
		}

		if (Array.isArray(node.content)) {
			for (const child of node.content) {
				count += this.countNodesInTree(child);
			}
		}

		return count;
	}

	private addRepairedNode(
		repairedNode: any,
		content: any[],
		originalType: string,
		context: string,
	): void {
		// Calculate expansion: how many nodes were created vs original 1 node
		const nodesCreated = Array.isArray(repairedNode)
			? repairedNode.reduce((sum, n) => sum + this.countNodesInTree(n), 0)
			: this.countNodesInTree(repairedNode);

		const expansion = nodesCreated - 1; // -1 because we're replacing 1 node

		this.repairs.push({
			type: repairedNode.type ? 'wrap' : 'unwrap',
			from: originalType,
			to: repairedNode.type || '❌',
			context,
			expansion, // Track how many extra nodes were created
		});

		log(
			`🔧 Auto-repair: ${originalType} → ${repairedNode.type || '❌'} in ${context}${expansion > 0 ? ` (+${expansion} node${expansion !== 1 ? 's' : ''})` : ''}`,
		);

		if (Array.isArray(repairedNode)) {
			content.push(...repairedNode);
		} else {
			content.push(repairedNode);
		}
	}

	private handleStructuralError(
		node: any,
		parentType: string | undefined,
		content: any[],
	): boolean {
		// Handle tableCell/tableHeader appearing in table (should be in tableRow)
		if (parentType === 'table' && (node.type === 'tableCell' || node.type === 'tableHeader')) {
			const lastRow = content[content.length - 1] as any;
			if (lastRow && lastRow.type === 'tableRow') {
				log(`🔧 Fixing: Adding ${node.type} to previous tableRow (LLM closed row prematurely)`);
				if (!lastRow.content) lastRow.content = [];
				lastRow.content.push(node);
				return true;
			} else {
				log(
					`⚠️  Skipping invalid node type "${node.type}" in "${parentType || 'doc'}" context (no tableRow to attach to)`,
				);
				return true;
			}
		}

		// Handle tableRow appearing at doc level (should be in table)
		if (parentType === 'doc' && node.type === 'tableRow') {
			log(`🔧 Fixing: Wrapping tableRow in table (LLM output fragment at doc level)`);
			const lastNode = content[content.length - 1] as any;
			if (lastNode && lastNode.type === 'table') {
				if (!lastNode.content) lastNode.content = [];
				lastNode.content.push(node);
			} else {
				content.push({
					type: 'table',
					attrs: { isNumberColumnEnabled: false },
					content: [node],
				});
			}
			return true;
		}

		// Handle tableCell/tableHeader appearing at doc level (should be in tableRow in table)
		if (parentType === 'doc' && (node.type === 'tableCell' || node.type === 'tableHeader')) {
			log(`🔧 Fixing: Wrapping ${node.type} in tableRow and table`);
			const lastNode = content[content.length - 1] as any;
			if (lastNode && lastNode.type === 'table' && lastNode.content?.length > 0) {
				const lastRow = lastNode.content[lastNode.content.length - 1];
				if (lastRow && lastRow.type === 'tableRow') {
					if (!lastRow.content) lastRow.content = [];
					lastRow.content.push(node);
				} else {
					lastNode.content.push({ type: 'tableRow', content: [node] });
				}
			} else {
				content.push({
					type: 'table',
					attrs: { isNumberColumnEnabled: false },
					content: [{ type: 'tableRow', content: [node] }],
				});
			}
			return true;
		}

		return false;
	}

	private parseContent(parentType?: string): Array<any> {
		const content: any[] = [];

		while (this.currentToken.type !== 'RIGHT_BRACKET' && this.currentToken.type !== 'EOF') {
			// Skip commas and unexpected closing braces (malformed JSON)
			if (this.currentToken.type === 'COMMA' || this.currentToken.type === 'RIGHT_BRACE') {
				this.advance();
				continue;
			}

			if (this.currentToken.type === 'LEFT_BRACE') {
				const node = this.parseContentNode(parentType);
				if (node) {
					if (node.type && !this.isValidChildType(node.type, parentType)) {
						const repairedNode = this.tryRepairNode(node, parentType);
						if (repairedNode) {
							this.addRepairedNode(repairedNode, content, node.type, parentType || 'doc');
						} else if (!this.handleStructuralError(node, parentType, content)) {
							log(
								`⚠️  Skipping invalid node type "${node.type}" in "${parentType || 'doc'}" context at position ${this.currentToken.position}`,
							);
						}
					} else {
						content.push(node);
					}
				}
			} else {
				// Skip unexpected tokens (but don't advance on delimiters to avoid infinite loop)
				this.skipValue();
			}
		}

		return content;
	}

	private parseMarks(): any[] {
		this.expect('LEFT_BRACKET');
		this.advance();

		const marks: any[] = [];

		while (this.currentToken.type !== 'RIGHT_BRACKET' && this.currentToken.type !== 'EOF') {
			if (this.currentToken.type === 'LEFT_BRACE') {
				this.advance();
				const mark: any = {};

				// @ts-expect-error - TypeScript is not able to infer currentToken is dynamic
				while (this.currentToken.type !== 'RIGHT_BRACE' && this.currentToken.type !== 'EOF') {
					this.expect('STRING');
					const key = this.currentToken.value as string;
					this.advance();

					// Handle malformed JSON: key without value
					// @ts-expect-error - TypeScript is not able to infer currentToken is dynamic
					if (this.currentToken.type === 'COMMA') {
						log(
							`⚠️  Skipping malformed key "${key}" without value in marks at position ${this.currentToken.position}`,
						);
						this.advance();
						continue;
					}

					this.expect('COLON');
					this.advance();

					if (key === 'type') {
						// @ts-expect-error - TypeScript is not able to infer currentToken is dynamic
						if (this.currentToken.type === 'STRING') {
							mark.type = this.currentToken.value;
							this.advance();
						} else {
							this.skipValue();
						}
					} else if (key === 'attrs') {
						if (this.currentToken.type === 'LEFT_BRACE') {
							mark.attrs = this.parseAttrs();
						} else {
							this.skipValue();
						}
					} else {
						this.skipValue();
					}

					// @ts-expect-error - TypeScript is not able to infer currentToken is dynamic
					if (this.currentToken.type === 'COMMA') {
						this.advance();
					}
				}

				if (this.currentToken.type === 'RIGHT_BRACE') {
					this.advance();
				}

				marks.push(mark);
			} else {
				this.skipValue();
			}

			if (this.currentToken.type === 'COMMA') {
				this.advance();
			}
		}

		if (this.currentToken.type === 'RIGHT_BRACKET') {
			this.advance();
		}

		return marks;
	}

	private parseContentNode(_parentType?: string): any {
		this.expect('LEFT_BRACE');
		this.advance();

		const node: any = {};

		while (this.currentToken.type !== 'RIGHT_BRACE' && this.currentToken.type !== 'EOF') {
			this.expect('STRING');
			const key = this.currentToken.value as string;
			this.advance();

			// Handle malformed JSON: key without value (e.g., "text", instead of "text": "value")
			if (this.currentToken.type === 'COMMA') {
				log(
					`⚠️  Skipping malformed key "${key}" without value at position ${this.currentToken.position}`,
				);
				this.advance();
				continue;
			}

			this.expect('COLON');
			this.advance();

			if (key === 'type') {
				this.expect('STRING');
				node.type = this.currentToken.value;
				this.advance();
			} else if (key === 'content') {
				this.expect('LEFT_BRACKET');
				this.advance();
				node.content = this.parseContent(node.type);
				if (this.currentToken.type === 'RIGHT_BRACKET') {
					this.advance();
				}
			} else if (key === 'attrs') {
				if (this.currentToken.type === 'LEFT_BRACE') {
					node.attrs = this.parseAttrs();
				} else {
					this.skipValue();
				}
			} else if (key === 'text') {
				if (this.currentToken.type === 'STRING') {
					node.text = this.currentToken.value;
					this.advance();
				} else {
					this.skipValue();
				}
			} else if (key === 'marks') {
				if (this.currentToken.type === 'LEFT_BRACKET') {
					node.marks = this.parseMarks();
				} else {
					this.skipValue();
				}
			} else {
				if (this.currentToken.type === 'STRING') {
					node[key] = this.currentToken.value;
					this.advance();
				} else if (this.currentToken.type === 'NUMBER') {
					node[key] = Number(this.currentToken.value);
					this.advance();
				} else if (this.currentToken.type === 'BOOLEAN') {
					node[key] = this.currentToken.value === 'true';
					this.advance();
				} else if (this.currentToken.type === 'NULL') {
					node[key] = null;
					this.advance();
				} else {
					this.skipValue();
				}
			}

			// @ts-expect-error - TypeScript is not able to infer currentToken is dynamic
			if (this.currentToken.type === 'COMMA') {
				this.advance();
			}
		}

		if (this.currentToken.type === 'RIGHT_BRACE') {
			this.advance();
		}

		return node;
	}

	private parseAttrs(): any {
		this.expect('LEFT_BRACE');
		this.advance();

		const attrs: any = {};

		while (this.currentToken.type !== 'RIGHT_BRACE' && this.currentToken.type !== 'EOF') {
			this.expect('STRING');
			const key = this.currentToken.value as string;
			this.advance();

			// Handle malformed JSON: key without value
			if (this.currentToken.type === 'COMMA') {
				log(
					`⚠️  Skipping malformed key "${key}" without value in attrs at position ${this.currentToken.position}`,
				);
				this.advance();
				continue;
			}

			this.expect('COLON');
			this.advance();

			if (this.currentToken.type === 'STRING') {
				attrs[key] = this.currentToken.value;
				this.advance();
			} else if (this.currentToken.type === 'NUMBER') {
				attrs[key] = Number(this.currentToken.value);
				this.advance();
			} else if (this.currentToken.type === 'BOOLEAN') {
				attrs[key] = this.currentToken.value === 'true';
				this.advance();
			} else if (this.currentToken.type === 'NULL') {
				attrs[key] = null;
				this.advance();
			} else {
				this.skipValue();
			}

			// @ts-expect-error - TypeScript is not able to infer currentToken is dynamic
			if (this.currentToken.type === 'COMMA') {
				this.advance();
			}
		}

		if (this.currentToken.type === 'RIGHT_BRACE') {
			this.advance();
		}

		return attrs;
	}

	private skipValue(): void {
		switch (this.currentToken.type) {
			case 'STRING':
			case 'NUMBER':
			case 'BOOLEAN':
			case 'NULL':
				this.advance();
				break;

			case 'LEFT_BRACE':
				this.skipObject();
				break;

			case 'LEFT_BRACKET':
				this.skipArray();
				break;

			case 'RIGHT_BRACE':
			case 'RIGHT_BRACKET':
			case 'COMMA':
			case 'EOF':
				// Malformed JSON - delimiter where value expected
				break;

			default:
				throw new Error(
					`Unexpected token ${this.currentToken.type} at position ${this.currentToken.position}`,
				);
		}
	}

	private skipObject(): void {
		this.expect('LEFT_BRACE');
		this.advance();

		while (this.currentToken.type !== 'RIGHT_BRACE' && this.currentToken.type !== 'EOF') {
			// Skip key
			this.expect('STRING');
			this.advance();

			// Skip colon
			this.expect('COLON');
			this.advance();

			// Skip value
			this.skipValue();

			// Handle comma
			if (this.currentToken.type === 'COMMA') {
				this.advance();
			}
		}

		this.expect('RIGHT_BRACE');
		this.advance();
	}

	private skipArray(): void {
		this.expect('LEFT_BRACKET');
		this.advance();

		while (this.currentToken.type !== 'RIGHT_BRACKET' && this.currentToken.type !== 'EOF') {
			this.skipValue();

			if (this.currentToken.type === 'COMMA') {
				this.advance();
			}
		}

		this.expect('RIGHT_BRACKET');
		this.advance();
	}

	parse(): DocNode {
		const firstDoc = this.parseDoc();
		const allContent = [...firstDoc.content];

		// Merge document fragments (LLM often outputs multiple fragments)
		while (this.currentToken.type !== 'EOF') {
			if (this.currentToken.type === 'COMMA') {
				this.advance();
				continue;
			}

			if (this.currentToken.type === 'LEFT_BRACE') {
				const fragment = this.parseContentNode();
				if (fragment && typeof fragment === 'object') {
					if (fragment.type && !this.isValidChildType(fragment.type, 'doc')) {
						const repairedNode = this.tryRepairNode(fragment, 'doc');
						if (repairedNode) {
							this.addRepairedNode(repairedNode, allContent, fragment.type, 'doc');
						} else if (!this.handleStructuralError(fragment, 'doc', allContent)) {
							log(`⚠️  Skipping invalid fragment type "${fragment.type}" at doc level`);
						}
					} else {
						allContent.push(fragment);
					}
				}
			} else {
				this.advance();
			}
		}

		return { type: 'doc', version: firstDoc.version, content: allContent };
	}
}
